import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Calendar, 
  Search, 
  Check, 
  X, 
  Clock, 
  Filter, 
  Plus, 
  FileText,
  AlertCircle,
  MoreVertical,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function DemandesManagement() {
  const { hasPermission } = usePermissions();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('tous');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reqRes, empRes] = await Promise.all([
        axios.get(`${API}/rh/demandes`, { headers: headers() }),
        axios.get(`${API}/rh/employes`, { headers: headers() })
      ]);
      setRequests(reqRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const matchesSearch = `${req.employe?.prenom} ${req.employe?.nom}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'tous' || req.statut === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [requests, searchTerm, filterStatus]);

  const handleStatusUpdate = async (id, status) => {
    const actionText = status === 'validee' ? 'valider' : 'rejeter';
    const result = await Swal.fire({
      title: `Voulez-vous ${actionText} cette demande ?`,
      input: 'textarea',
      inputLabel: 'Commentaire (optionnel)',
      inputPlaceholder: 'Saisissez votre commentaire...',
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
      confirmButtonColor: status === 'validee' ? '#38A169' : '#E53E3E'
    });

    if (result.isConfirmed) {
      try {
        await axios.patch(`${API}/rh/demandes/${id}/statut`, { 
          statut: status, 
          commentaireRH: result.value 
        }, { headers: headers() });
        Swal.fire('Mis à jour !', '', 'success');
        fetchData();
      } catch (err) {
        Swal.fire('Erreur', 'Impossible de mettre à jour le statut.', 'error');
      }
    }
  };

  const handleAddRequest = () => {
    Swal.fire({
      title: 'Nouvelle Demande',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Employé *</label>
            <select id="swal-emp" class="rh-modal-input">
              <option value="">Sélectionner...</option>
              ${employees.map(e => `<option value="${e._id}">${e.prenom} ${e.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Type *</label>
            <select id="swal-type" class="rh-modal-input">
              <option value="conge">Congé annuel</option>
              <option value="maladie">Maladie</option>
              <option value="absence">Absence</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div class="rh-form-row">
            <div class="rh-input-group">
              <label>Date début *</label>
              <input id="swal-start" type="date" class="rh-modal-input">
            </div>
            <div class="rh-input-group">
              <label>Date fin *</label>
              <input id="swal-end" type="date" class="rh-modal-input">
            </div>
          </div>
          <div class="rh-input-group">
            <label>Commentaire</label>
            <textarea id="swal-comment" class="rh-modal-input" placeholder="Raison de la demande..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Soumettre',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const employe = document.getElementById('swal-emp').value;
        const type = document.getElementById('swal-type').value;
        const dateDebut = document.getElementById('swal-start').value;
        const dateFin = document.getElementById('swal-end').value;
        const commentaireRH = document.getElementById('swal-comment').value;

        if (!employe || !dateDebut || !dateFin) {
          Swal.showValidationMessage('Veuillez remplir les champs obligatoires');
          return false;
        }
        return { employe, type, dateDebut, dateFin, commentaireRH, statut: 'en_attente' };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(`${API}/rh/demandes`, result.value, { headers: headers() });
          Swal.fire('Envoyé !', 'La demande a été enregistrée.', 'success');
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible de créer la demande.', 'error');
        }
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ background: '#38A169', width: '45px', height: '45px', borderRadius: '12px' }}>
              <Calendar size={24} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Demandes RH</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>Congés, absences et maladies</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select 
              className="rh-modal-input" 
              style={{ width: 'auto', padding: '0 12px', fontSize: '13px' }}
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="tous">Tous les statuts</option>
              <option value="en_attente">En attente</option>
              <option value="validee">Validées</option>
              <option value="rejetee">Rejetées</option>
            </select>
            <div className="rh-search-wrapper" style={{ width: '250px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher par employé..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddRequest}>
              <Plus size={18} /> Nouvelle
            </button>
          </div>
        </div>

        <div className="rh-table-container">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Type</th>
                <th>Période</th>
                <th>Durée</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map(req => (
                <tr key={req._id}>
                  <td>
                    <div className="rh-user-cell">
                      <div className="rh-avatar">{(req.employe?.prenom?.[0] || '') + (req.employe?.nom?.[0] || '')}</div>
                      <div className="rh-user-info">
                        <span className="rh-user-name">{req.employe?.prenom} {req.employe?.nom}</span>
                        <span className="rh-user-role">{req.employe?.poste?.nom || '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td><span className="rh-type-badge">{req.type}</span></td>
                  <td>
                    <div className="rh-period-text">
                      <Clock size={13} /> 
                      {new Date(req.dateDebut).toLocaleDateString()} au {new Date(req.dateFin).toLocaleDateString()}
                    </div>
                  </td>
                  <td><strong>{req.nbJoursConsommes || 0} j</strong></td>
                  <td>
                    <span className={`rh-status-badge ${req.statut === 'validee' ? 'approved' : req.statut === 'rejetee' ? 'rejected' : 'pending'}`}>
                      {req.statut === 'en_attente' ? 'À valider' : req.statut}
                    </span>
                  </td>
                  <td>
                    <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                      {req.statut === 'en_attente' && (
                        <>
                          <button className="rh-action-btn edit" style={{ color: '#38A169', borderColor: '#38A169' }} onClick={() => handleStatusUpdate(req._id, 'validee')}>
                            <Check size={16} />
                          </button>
                          <button className="rh-action-btn delete" onClick={() => handleStatusUpdate(req._id, 'rejetee')}>
                            <X size={16} />
                          </button>
                        </>
                      )}
                      <button className="rh-action-btn" style={{ background: '#F8FAFC' }} title="Détails" onClick={() => Swal.fire('Détails', req.commentaireRH || 'Aucun commentaire.', 'info')}>
                        <FileText size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRequests.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>Aucune demande trouvée.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
